import type { LocalizedDict } from '../index'
import type {
  SellerHomeActionKind,
  SellerMaturity,
} from '../../features/seller/home/seller-home-model'

export type SellerHomeStrings = {
  eyebrow: string
  title: string
  description: string
  viewOrders: string
  createProduct: string
  sections: {
    focus: string
    pulse: string
    growth: string
    performance: string
    health: string
    catalogue: string
  }
  hints: {
    focus: string
    growth: string
    performance: string
    health: string
    catalogue: string
  }
  metrics: {
    grossSales: string
    orders: string
    readyToShip: string
    activeListings: string
    last30Days: string
    listingIssues: (count: number) => string
  }
  focusHealthyTitle: string
  focusHealthyBody: string
  action: Record<
    SellerHomeActionKind,
    { title: (count: number) => string; body: string; cta: string }
  >
  growth: Record<SellerMaturity, Array<{ title: string; body: string }>>
  completed: string
  next: string
  unavailable: string
  retry: string
  loading: string
  noSalesTitle: string
  noSalesBody: string
  noStores: string
  healthy: string
  degraded: string
  failing: string
  viewReport: string
  manageStores: string
  viewCatalogue: string
  chartSummary: (orders: number, gross: string) => string
  ordersCount: (count: number) => string
  vsPreviousPeriod: (pct: number) => string
  table: { date: string; orders: string; grossSales: string }
}

const en: SellerHomeStrings = {
  eyebrow: 'SELLER HOME',
  title: 'Your business, ready for today',
  description:
    'See what needs attention, how sales are moving and the clearest next step for growth.',
  viewOrders: 'View orders',
  createProduct: 'Create product',
  sections: {
    focus: "Today's focus",
    pulse: 'Business pulse',
    growth: 'Your growth plan',
    performance: 'Sales performance',
    health: 'Store health',
    catalogue: 'New in your catalogue',
  },
  hints: {
    focus: 'The highest-impact work across your connected commerce operation.',
    growth: 'A clear path based on the current state of your seller workspace.',
    performance: 'Daily gross sales across the last 30 operational days.',
    health: 'Connection and synchronization signals for your sales channels.',
    catalogue: 'Seller-ready products curated by the PEXIFY catalogue team.',
  },
  metrics: {
    grossSales: 'Gross sales',
    orders: 'Orders',
    readyToShip: 'Ready for shipment',
    activeListings: 'Active listings',
    last30Days: 'Last 30 days',
    listingIssues: (count) => `${count} ${count === 1 ? 'issue' : 'issues'}`,
  },
  focusHealthyTitle: 'Everything important is moving',
  focusHealthyBody:
    'No connection, order or listing blockers need your attention right now.',
  action: {
    'store-failing': {
      title: (count) =>
        `${count} ${count === 1 ? 'store needs' : 'stores need'} attention`,
      body: 'Restore authorization or synchronization before the next order arrives.',
      cta: 'Review stores',
    },
    'seller-approval': {
      title: (count) =>
        `${count} ${count === 1 ? 'order is' : 'orders are'} waiting for approval`,
      body: 'Customer orders cannot progress until they are reviewed.',
      cta: 'Review orders',
    },
    'ready-for-shipping': {
      title: (count) =>
        `${count} ${count === 1 ? 'order is' : 'orders are'} ready to ship`,
      body: 'Complete the handoff while the fulfillment window is still healthy.',
      cta: 'Open shipping queue',
    },
    'listing-issues': {
      title: (count) => `${count} listing ${count === 1 ? 'issue' : 'issues'} to resolve`,
      body: 'Repair SKU and mapping gaps that can interrupt order intake.',
      cta: 'Review listings',
    },
    'connect-store': {
      title: () => 'Connect your first sales channel',
      body: 'Bring orders and listings into one governed workspace.',
      cta: 'Connect a store',
    },
    'create-listing': {
      title: () => 'Publish your first active listing',
      body: 'Choose a seller-ready product and start building your assortment.',
      cta: 'Explore catalogue',
    },
  },
  growth: {
    new: [
      { title: 'Connect a sales channel', body: 'Link Etsy, Shopify or another supported store.' },
      { title: 'Choose your first product', body: 'Explore products already prepared for sellers.' },
      { title: 'Publish your first listing', body: 'Turn the selected product into a live offer.' },
    ],
    'connected-empty': [
      { title: 'Sales channel connected', body: 'Your commerce connection is ready.' },
      { title: 'Build an active assortment', body: 'Add or repair the first listing buyers can purchase.' },
      { title: 'Review catalogue opportunities', body: 'Find the next product that fits your storefront.' },
    ],
    established: [
      { title: 'Connected sales channels', body: 'Your active stores are governed from one place.' },
      { title: 'Improve the highest-impact item', body: 'Clear today’s leading operational or listing blocker.' },
      { title: 'Expand your assortment', body: 'Explore one new seller-ready product opportunity.' },
    ],
    unknown: [
      { title: 'Check connected channels', body: 'We are confirming your store state.' },
      { title: 'Review active listings', body: 'Listing progress appears when the source is available.' },
      { title: 'Explore your catalogue', body: 'Seller-ready products remain available to browse.' },
    ],
  },
  completed: 'Completed',
  next: 'Next step',
  unavailable: 'Unavailable',
  retry: 'Try again',
  loading: 'Loading',
  noSalesTitle: 'Sales history will appear here',
  noSalesBody:
    'Daily performance becomes available after your first non-cancelled order.',
  noStores: 'No connected store health data yet.',
  healthy: 'Healthy',
  degraded: 'Needs attention',
  failing: 'Action required',
  viewReport: 'View report',
  manageStores: 'Manage stores',
  viewCatalogue: 'View catalogue',
  chartSummary: (orders, gross) =>
    `Last 30 days: ${orders} orders and ${gross} in gross sales.`,
  ordersCount: (count) => `${count} orders`,
  vsPreviousPeriod: (pct) => `${pct}% vs previous period`,
  table: { date: 'Date', orders: 'Orders', grossSales: 'Gross sales' },
}

const tr: SellerHomeStrings = {
  eyebrow: 'SATICI ANA SAYFA',
  title: 'İşiniz bugün için hazır',
  description:
    'Nelerin ilgi beklediğini, satışların nasıl ilerlediğini ve büyüme için en net sonraki adımı görün.',
  viewOrders: 'Siparişleri gör',
  createProduct: 'Ürün oluştur',
  sections: {
    focus: 'Bugünün odağı',
    pulse: 'İş nabzı',
    growth: 'Büyüme planınız',
    performance: 'Satış performansı',
    health: 'Mağaza sağlığı',
    catalogue: 'Kataloğunuzdaki yeniler',
  },
  hints: {
    focus: 'Bağlı ticaret operasyonunuzdaki en yüksek etkili işler.',
    growth: 'Satıcı çalışma alanınızın mevcut durumuna göre net bir yol.',
    performance: 'Son 30 operasyon günündeki günlük brüt satışlar.',
    health: 'Satış kanallarınızın bağlantı ve senkronizasyon sinyalleri.',
    catalogue: 'PEXIFY katalog ekibinin satıcılar için hazırladığı ürünler.',
  },
  metrics: {
    grossSales: 'Brüt satış',
    orders: 'Sipariş',
    readyToShip: 'Gönderime hazır',
    activeListings: 'Aktif ilan',
    last30Days: 'Son 30 gün',
    listingIssues: (count) => `${count} sorun`,
  },
  focusHealthyTitle: 'Önemli olan her şey ilerliyor',
  focusHealthyBody:
    'Şu anda ilginizi bekleyen bağlantı, sipariş veya ilan engeli yok.',
  action: {
    'store-failing': {
      title: (count) => `${count} mağaza ilgi bekliyor`,
      body: 'Bir sonraki sipariş gelmeden yetkilendirme veya senkronizasyonu düzeltin.',
      cta: 'Mağazaları incele',
    },
    'seller-approval': {
      title: (count) => `${count} sipariş onay bekliyor`,
      body: 'Müşteri siparişleri incelenmeden ilerleyemez.',
      cta: 'Siparişleri incele',
    },
    'ready-for-shipping': {
      title: (count) => `${count} sipariş gönderime hazır`,
      body: 'Operasyon süresi sağlıklıyken gönderim adımını tamamlayın.',
      cta: 'Gönderim kuyruğunu aç',
    },
    'listing-issues': {
      title: (count) => `Çözülmesi gereken ${count} ilan sorunu var`,
      body: 'Sipariş akışını kesebilecek SKU ve eşleme boşluklarını giderin.',
      cta: 'İlanları incele',
    },
    'connect-store': {
      title: () => 'İlk satış kanalınızı bağlayın',
      body: 'Siparişleri ve ilanları tek, yönetilen çalışma alanına taşıyın.',
      cta: 'Mağaza bağla',
    },
    'create-listing': {
      title: () => 'İlk aktif ilanınızı yayınlayın',
      body: 'Satıcıya hazır bir ürün seçin ve ürün gamınızı oluşturmaya başlayın.',
      cta: 'Kataloğu keşfet',
    },
  },
  growth: {
    new: [
      { title: 'Bir satış kanalı bağlayın', body: 'Etsy, Shopify veya desteklenen başka bir mağazayı bağlayın.' },
      { title: 'İlk ürününüzü seçin', body: 'Satıcılar için hazırlanmış ürünleri keşfedin.' },
      { title: 'İlk ilanınızı yayınlayın', body: 'Seçtiğiniz ürünü canlı bir teklife dönüştürün.' },
    ],
    'connected-empty': [
      { title: 'Satış kanalı bağlandı', body: 'Ticaret bağlantınız hazır.' },
      { title: 'Aktif bir ürün gamı oluşturun', body: 'Alıcıların satın alabileceği ilk ilanı ekleyin veya düzeltin.' },
      { title: 'Katalog fırsatlarını inceleyin', body: 'Mağazanıza uyan bir sonraki ürünü bulun.' },
    ],
    established: [
      { title: 'Bağlı satış kanalları', body: 'Aktif mağazalarınız tek yerden yönetiliyor.' },
      { title: 'En yüksek etkili işi iyileştirin', body: 'Bugünün öncelikli operasyon veya ilan engelini temizleyin.' },
      { title: 'Ürün gamınızı genişletin', body: 'Satıcıya hazır yeni bir ürün fırsatı keşfedin.' },
    ],
    unknown: [
      { title: 'Bağlı kanalları kontrol edin', body: 'Mağaza durumunuz doğrulanıyor.' },
      { title: 'Aktif ilanları inceleyin', body: 'Kaynak hazır olduğunda ilan ilerlemesi görünecek.' },
      { title: 'Kataloğunuzu keşfedin', body: 'Satıcıya hazır ürünler keşfedilmeye devam ediyor.' },
    ],
  },
  completed: 'Tamamlandı',
  next: 'Sonraki adım',
  unavailable: 'Kullanılamıyor',
  retry: 'Tekrar dene',
  loading: 'Yükleniyor',
  noSalesTitle: 'Satış geçmişi burada görünecek',
  noSalesBody:
    'İlk iptal edilmemiş siparişinizden sonra günlük performans gösterilir.',
  noStores: 'Henüz bağlı mağaza sağlık verisi yok.',
  healthy: 'Sağlıklı',
  degraded: 'İlgi gerekiyor',
  failing: 'İşlem gerekiyor',
  viewReport: 'Raporu gör',
  manageStores: 'Mağazaları yönet',
  viewCatalogue: 'Kataloğu gör',
  chartSummary: (orders, gross) =>
    `Son 30 gün: ${orders} sipariş ve ${gross} brüt satış.`,
  ordersCount: (count) => `${count} sipariş`,
  vsPreviousPeriod: (pct) => `önceki döneme göre %${pct}`,
  table: { date: 'Tarih', orders: 'Sipariş', grossSales: 'Brüt satış' },
}

export const SELLER_HOME_T: LocalizedDict<SellerHomeStrings> = { en, tr }
